"use client"

import { useEffect, useRef, useMemo } from "react"
import { useAuth } from "@/contexts/auth-context"
import { useWorkflow } from "@/context/workflow-context-simple"
import { env } from '@/lib/env'

// Resolve once; guard against missing/invalid URL
const useAutomationsOrigin = () => useMemo(() => {
  try {
    return env.automationsUrl ? new URL(env.automationsUrl).origin : null
  } catch {
    console.error("AutomationModal: Invalid env.automationsUrl")
    return null
  }
}, [])

interface AutomationModalProps {
  isOpen: boolean
  onClose: () => void
}

export function AutomationModal({ isOpen, onClose }: AutomationModalProps) {
  const { token, handleIframeTokenRequest, sendTokenToIframe } = useAuth()
  const { configuration } = useWorkflow()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const expectedOrigin = useAutomationsOrigin()

  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const hasAutomationsLinkPermission = authorizedActions.includes("AutomationsLink")

  useEffect(() => {
    if (!isOpen || !hasAutomationsLinkPermission) return

    const handleMessage = (event: MessageEvent) => {
      try {
        // Validate the message origin
        if (!expectedOrigin) {
          console.warn("AutomationModal: Cannot validate origin - invalid automations URL")
          return
        }
        if (event.origin !== expectedOrigin) {
          console.warn("Ignoring message from unexpected origin:", event.origin)
          return
        }
            
        const data = event.data

        // Handle REQUEST_TOKEN message from iframe
        if (data && data.action === "REQUEST_TOKEN" && iframeRef.current?.contentWindow && expectedOrigin) {
          // Let the auth context handle the token request (bridge logic)
          handleIframeTokenRequest(iframeRef.current.contentWindow, expectedOrigin)
        }
      } catch (error) {
        console.error("AutomationModal: Error processing message from iframe:", error)
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage)
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage)
      }
    }
  }, [isOpen, handleIframeTokenRequest, expectedOrigin])

  // Send token to iframe when token changes and iframe is loaded
  useEffect(() => {
    if (isOpen && hasAutomationsLinkPermission && token && iframeRef.current?.contentWindow && expectedOrigin) {
      const timer = setTimeout(() => {
        sendTokenToIframe(iframeRef.current!.contentWindow!, expectedOrigin)
      }, 500) // Small delay to ensure iframe is ready

      return () => clearTimeout(timer)
    }
  }, [isOpen, hasAutomationsLinkPermission, token, expectedOrigin, sendTokenToIframe])

  const handleIframeLoad = () => {
    // Send token when iframe loads
    if (hasAutomationsLinkPermission && token && iframeRef.current?.contentWindow && expectedOrigin) {
      sendTokenToIframe(iframeRef.current.contentWindow, expectedOrigin)
    }
  }

  if (!isOpen || !hasAutomationsLinkPermission) return null

  return (
    <div className="fixed inset-0 z-50 bg-white">
      {/* Floating close button overlay */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 bg-white hover:bg-gray-100 rounded-full shadow-lg transition-colors border border-gray-200"
        aria-label="Close"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      
      {/* Iframe content - full screen */}
      <iframe
        ref={iframeRef}
        src={env.automationsUrl}
        className="w-full h-full border-0"
        onLoad={handleIframeLoad}
        title="Automations"
      />
    </div>
  )
}
